import React from 'react'

import {
    Button,
    UiDeleteIcon,
    UiPlusIcon,
    VsInformationIcon,
} from '@octane/spark'
import {
    EMPTY_OPE_UNIT,
    INITIAL_OPE_UNITS,
    OpeUnitValues,
} from 'form/fields/opeEquipmentFields'
import { setFieldValueAndTouched } from 'form/fields/utils/fieldUtils'
import useOpeCollateralLimits from 'hooks/useOpeCollateralLimits'
import useOpeMultiUnitEnabled from 'hooks/useOpeMultiUnitEnabled'
import { useTheme } from 'styled-components'
import { formatToRoundedDollar } from 'utils/formatToRoundedDollar'

import {
    Actions,
    EquipmentNotice,
    EquipmentNoticeText,
    RemoveUnitButton,
    SubtotalAmount,
    SubtotalBand,
    SubtotalLabel,
    UnitField,
    UnitLabel,
    UnitRow,
} from './OpeEquipmentSelection.styled'
import VehicleSearchSelection from './VehicleSearchSelection'

interface OpeEquipmentSelectionProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: any
    prefilled?: boolean
}

const defaultProps = {
    prefilled: false,
}

const UNITS_FIELD = 'vehicle.opeUnits'

export default function OpeEquipmentSelection({
    formik,
    prefilled,
}: Readonly<OpeEquipmentSelectionProps>) {
    const theme = useTheme()
    const { isMultiUnitEnabled } = useOpeMultiUnitEnabled()
    const { maxItems } = useOpeCollateralLimits()
    const storedUnits = formik.values.vehicle?.opeUnits

    const units: Partial<OpeUnitValues>[] = storedUnits?.length
        ? storedUnits
        : INITIAL_OPE_UNITS

    const visibleUnits = prefilled
        ? units
        : isMultiUnitEnabled
          ? units
          : units.slice(0, 1)

    const canAdd = !prefilled && isMultiUnitEnabled && units.length < maxItems
    const canRemove = !prefilled && isMultiUnitEnabled && units.length > 1

    const msrpSubtotal = units.reduce((total, unit) => {
        const msrp = Number(unit.msrp)

        return total + (Number.isFinite(msrp) ? msrp : 0)
    }, 0)

    const addUnit = () => {
        setFieldValueAndTouched(formik, UNITS_FIELD, [
            ...units,
            { ...EMPTY_OPE_UNIT },
        ])
    }

    const removeUnit = (index: number) => {
        const remainingUnits = units.filter(
            (_, unitIndex) => unitIndex !== index,
        )

        setFieldValueAndTouched(formik, UNITS_FIELD, remainingUnits)

        // Removing the primary (first) unit promotes the next unit to
        // primary - resync the application-level `vehicle.vehicleType`
        // field so it still reflects the (new) primary unit rather than
        // the one that was just removed.
        if (index === 0) {
            formik.setFieldValue(
                'vehicle.vehicleType',
                remainingUnits[0]?.equipmentType ?? '',
            )
        }
    }

    return (
        <div data-testid="opeEquipmentSelection">
            <EquipmentNotice>
                <VsInformationIcon
                    color={theme.colors.alert.note}
                    width={18}
                    height={18}
                />
                <EquipmentNoticeText>
                    Our financing program covers new equipment only. All
                    equipment must be from the same OEM for this deal.
                </EquipmentNoticeText>
            </EquipmentNotice>

            {visibleUnits.map((unit, index) => (
                <UnitRow key={unit.selectedVehicleId || index}>
                    {(isMultiUnitEnabled || prefilled) && (
                        <UnitLabel
                            htmlFor={`${UNITS_FIELD}[${index}].selectedVehicleId`}
                        >
                            Make &amp; Model
                        </UnitLabel>
                    )}
                    <UnitField>
                        <VehicleSearchSelection
                            formik={formik}
                            unit={unit}
                            unitPath={`${UNITS_FIELD}[${index}]`}
                            prefilled={prefilled}
                            ariaLabel={`Make & Model, equipment ${index + 1}`}
                            showNotice={false}
                            isPrimaryUnit={index === 0}
                        />
                    </UnitField>
                    {canRemove && (
                        <RemoveUnitButton>
                            <Button
                                id={`removeOpeUnit-${index}`}
                                startIcon={UiDeleteIcon}
                                variant="errorOutlined"
                                aria-label={`Remove equipment ${index + 1}`}
                                onClick={() => removeUnit(index)}
                            />
                        </RemoveUnitButton>
                    )}
                </UnitRow>
            ))}

            {canAdd && (
                <Actions>
                    <Button
                        id="addOpeUnit"
                        endIcon={UiPlusIcon}
                        variant="white"
                        size="tiny"
                        onClick={addUnit}
                    >
                        Add another unit
                    </Button>
                </Actions>
            )}

            {(isMultiUnitEnabled || prefilled) && (
                <SubtotalBand data-testid="opeEquipmentSubtotal">
                    <SubtotalLabel>Equipment Subtotal</SubtotalLabel>
                    <SubtotalAmount>
                        {formatToRoundedDollar(msrpSubtotal)}
                    </SubtotalAmount>
                </SubtotalBand>
            )}
        </div>
    )
}

OpeEquipmentSelection.defaultProps = defaultProps
